import type { MeasureComponentProps, MeasureComponentState } from "@component/MeasureComponent";
import { MeasureComponent } from "@component/MeasureComponent";
import type { VideoSchema } from "@component/StructuredDataSchema";
import { StructuredDataSchema } from "@component/StructuredDataSchema";
import { ClickableBlock } from "@ui/components/ClickableBlock";
import { GoogleAnalytics4 } from "@utils/google-analytics-4";
import type { EventComponentType } from "@utils/google-analytics-4.type";
import autobind from "autobind-decorator";
import type React from "react";
import { InView } from "react-intersection-observer";

interface Props extends MeasureComponentProps {
	videoId: string;
	title: string;
	id?: string;
	width?: number | string;
	height?: number | string;
	className?: string;
	description: string;
	uploadDate: string;
	startTime?: number;
}

interface State extends MeasureComponentState {
	preconnected: boolean;
	iframe: boolean;
}

/**
 * @Shortcode
 *
 * @title "Youtube Video"
 * @shortcode "youtube_video"
 * @type "widget"
 * @lazyHydrate "whenVisible"
 */
export class YoutubeVideo extends MeasureComponent<Props, State> {
	protected productTypes = "";
	protected readonly componentType: EventComponentType = "widget";

	protected getInitialState(): State {
		return { ...super.getInitialState(), preconnected: false, iframe: false };
	}

	public render(): React.ReactNode {
		const { videoId, id, title, width, height, startTime = 0 } = this.props;
		const { preconnected, iframe, wasVisible } = this.state;

		const encodedVideoId = encodeURIComponent(videoId);
		const posterUrl =
			window.innerWidth < 640
				? `https://i.ytimg.com/vi/${encodedVideoId}/hqdefault.jpg`
				: `https://i.ytimg.com/vi/${encodedVideoId}/maxresdefault.jpg`;

		return (
			<>
				<link rel="preload" href={posterUrl} as="image" />
				{preconnected && (
					<>
						<link rel="preconnect" href="https://www.youtube.com" />
						<link rel="preconnect" href="https://www.google.com" />
					</>
				)}
				<InView {...GoogleAnalytics4.getInViewProps()} skip={wasVisible} onChange={this.onVisibilityChange}>
					<ClickableBlock
						id={id}
						onPointerOver={this.onPointerOver}
						onClick={this.onClick}
						className={`yt-lite ${iframe && "lyt-activated"}`}
						data-title={title}
						style={{ backgroundImage: `url(${posterUrl})` }}
					>
						<div className="lyt-title">{title}</div>
						<div className="lty-playbtn" />
						{iframe && (
							<iframe
								title={title}
								width={width}
								height={height}
								frameBorder="0"
								allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
								allowFullScreen
								src={`https://www.youtube.com/embed/${encodedVideoId}?start=${startTime}&autoplay=1`}
							/>
						)}
					</ClickableBlock>
				</InView>
				<StructuredDataSchema schema={this.getSchema()} />
			</>
		);
	}

	@autobind
	private onPointerOver(): void {
		const { preconnected } = this.state;

		if (preconnected) return;

		this.setState({
			preconnected: true,
		});
	}

	@autobind
	private onClick(): void {
		const { iframe } = this.state;

		if (iframe) return;

		this.setState({
			iframe: true,
		});
	}

	private getSchema(): VideoSchema {
		const { videoId, title, description, uploadDate } = this.props;
		const encodedVideoId = encodeURIComponent(videoId);

		return {
			"@context": "https://schema.org",
			"@type": "VideoObject",
			name: title,
			description,
			uploadDate,
			embedUrl: `https://www.youtube.com/embed/${encodedVideoId}`,
			contentUrl: `https://www.youtube.com/embed/${encodedVideoId}`,
			thumbnailUrl: [
				`https://i.ytimg.com/vi/${encodedVideoId}/hqdefault.jpg`,
				`https://i.ytimg.com/vi/${encodedVideoId}/maxresdefault.jpg`,
			],
		};
	}

	protected getComponentName(): string {
		return "YoutubeVideo";
	}
}

export default YoutubeVideo;
